import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { CarrierDTO } from '@invenco/shipping-interface';
import { WarehouseCarrierServiceDTO, WmsInstanceDTO } from '@invenco/warehousing-interface';

import { useDataTable, useRowNavigation } from '@/components-ant/data-table';

import { DEFAULT_PAGE_SIZE } from '../../../../constants';
import { useGateways } from '../../../../gateways/GatewayProvider';
import { Result } from '../../../../shared/helpers/Result';
import { useQueryWithInput, useGatewayMutation } from '../../../../shared/hooks/queries';
import { ComponentData, Page, Pagination } from '../../../../shared/types';

export type WarehouseCarrierServiceRow = WarehouseCarrierServiceDTO;

type Models = {
  isLoading: boolean;
  hasError: boolean;
  rows?: WarehouseCarrierServiceDTO[];
  pagination?: Pagination;
  total?: number;
  query?: string;
  isCreateWarehouseCarrierServiceModalOpen: boolean;
  wmsInstances?: WmsInstanceDTO[];
  isLoadingWmsInstances: boolean;
  carriers?: CarrierDTO[];
  isLoadingCarriers: boolean;
};

type Operations = {
  openCreateWarehouseCarrierServiceModal: () => void;
  closeCreateWarehouseCarrierServiceModal: () => void;
  search: (query: string) => void;
  navigate: (page: Page) => void;
  refresh: () => void;
  createWarehouseCarrierService: (
    warehouseCarrierService: Partial<WarehouseCarrierServiceDTO>,
  ) => Promise<Result>;
  onClickRow: (
    row: WarehouseCarrierServiceRow,
    event: React.MouseEvent<HTMLTableRowElement>,
  ) => void;
};

export function useWarehouseCarrierServiceListPage(): ComponentData<Models, Operations> {
  const { warehousingGateway, shippingGateway } = useGateways();
  const [tableState, setTableState] = useDataTable();
  const { onClickRow } = useRowNavigation<WarehouseCarrierServiceRow>({
    baseUrl: '/warehouseCarrierServices',
  });
  const [isCreateWarehouseCarrierServiceModalOpen, setIsCreateWarehouseCarrierServiceModalOpen] =
    useState(false);

  const { data: wmsInstances, isLoading: isLoadingWmsInstances } = useQuery({
    queryKey: ['wmsInstances'],
    queryFn: ({ signal }) => warehousingGateway.getWmsInstances(undefined, { signal }),
  });

  const { data, isLoading, isError, refetch } = useQueryWithInput({
    parentKey: 'warehouseCarrierServices',
    input: {
      take: tableState.page?.take || DEFAULT_PAGE_SIZE,
      cursor: tableState.page?.cursor,
      search: tableState.query,
    },
    query: (input, { signal }) => warehousingGateway.getWarehouseCarrierServices(input, { signal }),
  });

  const { data: carriers, isLoading: isLoadingCarriers } = useQueryWithInput({
    parentKey: 'carriers',
    input: {
      include: { carrierServices: true },
    },
    query: (input, { signal }) => shippingGateway.getCarriers(input, { signal }),
    select: (carrierData) => carrierData.items,
  });

  const { mutate: createWarehouseCarrierService } = useGatewayMutation({
    mutationFn: (warehouseCarrierService: Partial<WarehouseCarrierServiceDTO>) =>
      warehousingGateway.createWarehouseCarrierService(warehouseCarrierService),
    linkedQuery: ['warehouseCarrierServices'],
    linkResponseToQuery: false,
    successMessage: 'Warehouse carrier service added',
  });

  return {
    models: {
      isLoading,
      hasError: isError,
      rows: data?.items,
      pagination: data?.pagination,
      total: data?.total,
      query: tableState.query,
      isCreateWarehouseCarrierServiceModalOpen,
      wmsInstances,
      isLoadingWmsInstances,
      carriers,
      isLoadingCarriers,
    },
    operations: {
      search: (query) => setTableState({ query, page: undefined }),
      navigate: (page) => setTableState({ page }),
      refresh: refetch,
      openCreateWarehouseCarrierServiceModal: () =>
        setIsCreateWarehouseCarrierServiceModalOpen(true),
      closeCreateWarehouseCarrierServiceModal: () =>
        setIsCreateWarehouseCarrierServiceModalOpen(false),
      createWarehouseCarrierService,
      onClickRow,
    },
  };
}
